import { useEffect, useState } from 'react';
import { Check, Loader2, Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { saasApi } from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';
import { resolveFileUrl } from '@/utils/fileUrl';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import LoyaltyMemberCard, {
  LOYALTY_CARD_DESIGNS,
  type LoyaltyCardDesign,
} from '@/components/loyalty/LoyaltyMemberCard';

export type LoyaltyCardDesignDraft = {
  card_design: LoyaltyCardDesign;
  card_colour: string;
  /** Empty = use company logo on the card */
  card_logo: string;
  card_tagline: string;
  card_show_points: boolean;
  card_show_value: boolean;
  card_show_tier: boolean;
};

type TierPreview = { tier_name: string; badge_color?: string };

type Props = {
  open: boolean;
  onClose: () => void;
  initial: LoyaltyCardDesignDraft;
  shopName?: string;
  /** Company logo from Settings → Company (used when no card override) */
  companyLogo?: string;
  tiers?: TierPreview[];
  saving?: boolean;
  onSave: (draft: LoyaltyCardDesignDraft) => void | Promise<void>;
};

export default function LoyaltyCardDesignModal({
  open,
  onClose,
  initial,
  shopName = 'Your Shop',
  companyLogo = '',
  tiers = [],
  saving = false,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<LoyaltyCardDesignDraft>(initial);

  useEscapeToClose(() => {
    if (!saving) onClose();
  });

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  if (!open) return null;

  const previewTier = tiers.find((t) => (t.tier_name || '').trim())
    ? {
        tier_name:
          tiers[Math.min(1, tiers.length - 1)]?.tier_name || tiers[0].tier_name,
        badge_color:
          tiers[Math.min(1, tiers.length - 1)]?.badge_color || '#d4a017',
      }
    : null;

  const usingCompanyLogo = !(draft.card_logo || '').trim();
  const companyLogoUrl = resolveFileUrl(companyLogo);
  const customLogoUrl = resolveFileUrl(draft.card_logo);
  const logoPreview = usingCompanyLogo ? companyLogoUrl : customLogoUrl;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={() => {
        if (!saving) onClose();
      }}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Digital card design"
      >
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10 rounded-t-2xl">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Digital card design</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Layout, colour, logo and tagline for View Card and the customer portal
            </p>
          </div>
          <button
            type="button"
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-3 gap-2">
            {LOYALTY_CARD_DESIGNS.map((d) => {
              const selected = draft.card_design === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() =>
                    setDraft((prev) => ({
                      ...prev,
                      card_design: d.id,
                      card_colour:
                        !prev.card_colour ||
                        LOYALTY_CARD_DESIGNS.some((x) => x.defaultColour === prev.card_colour)
                          ? d.defaultColour
                          : prev.card_colour,
                    }))
                  }
                  className={`text-left rounded-xl border-2 p-3 transition-colors ${
                    selected
                      ? 'border-primary-500 bg-primary-50/50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div
                    className="h-8 rounded-lg mb-2"
                    style={{
                      background: `linear-gradient(135deg, ${d.defaultColour}, ${d.defaultColour}99)`,
                    }}
                  />
                  <p className="text-sm font-semibold text-gray-900">{d.label}</p>
                  <p className="text-[11px] text-gray-500">{d.blurb}</p>
                </button>
              );
            })}
          </div>

          <div className="grid sm:grid-cols-2 gap-5 items-start">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card colour</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    className="h-10 w-12 rounded border border-gray-200 cursor-pointer p-0.5 bg-white"
                    value={
                      draft.card_colour && /^#[0-9A-Fa-f]{6}$/.test(draft.card_colour)
                        ? draft.card_colour
                        : '#15803d'
                    }
                    onChange={(e) => setDraft((p) => ({ ...p, card_colour: e.target.value }))}
                  />
                  <input
                    className="input-field font-mono text-sm"
                    value={draft.card_colour}
                    onChange={(e) => setDraft((p) => ({ ...p, card_colour: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
                <input
                  className="input-field"
                  placeholder="e.g. Electronics Store"
                  value={draft.card_tagline}
                  onChange={(e) => setDraft((p) => ({ ...p, card_tagline: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Show on card</label>
                <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50/80 p-3">
                  {(
                    [
                      { key: 'card_show_points' as const, label: 'Points', hint: 'Balance e.g. 200 pts' },
                      { key: 'card_show_value' as const, label: 'Points value', hint: 'Cash equivalent under points' },
                      { key: 'card_show_tier' as const, label: 'Tier', hint: 'Only if tiers are defined' },
                    ] as const
                  ).map((opt) => (
                    <label
                      key={opt.key}
                      className="flex items-start gap-2.5 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={draft[opt.key]}
                        onChange={(e) =>
                          setDraft((p) => ({ ...p, [opt.key]: e.target.checked }))
                        }
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                        <span className="block text-[11px] text-gray-500">{opt.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Card logo</label>
                <p className="text-xs text-gray-500 mb-2">
                  Defaults to your company logo. Upload a different image only if you want a card-specific logo.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDraft((p) => ({ ...p, card_logo: '' }))}
                    className={`text-left rounded-xl border-2 p-3 transition-colors ${
                      usingCompanyLogo
                        ? 'border-primary-500 bg-primary-50/50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-lg border border-gray-200 bg-white overflow-hidden flex items-center justify-center mb-2">
                      {companyLogoUrl ? (
                        <img src={companyLogoUrl} alt="" className="w-full h-full object-contain p-0.5" />
                      ) : (
                        <span className="text-[9px] text-gray-400 text-center px-1">No company logo</span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-gray-900 flex items-center gap-1">
                      {usingCompanyLogo && <Check className="w-3.5 h-3.5 text-primary-600 shrink-0" />}
                      Company logo
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">From Settings → Company</p>
                  </button>

                  <div
                    className={`rounded-xl border-2 p-3 transition-colors ${
                      !usingCompanyLogo
                        ? 'border-primary-500 bg-primary-50/50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-lg border border-gray-200 bg-white overflow-hidden flex items-center justify-center mb-2">
                      {customLogoUrl ? (
                        <img src={customLogoUrl} alt="" className="w-full h-full object-contain p-0.5" />
                      ) : (
                        <Upload className="w-4 h-4 text-gray-300" />
                      )}
                    </div>
                    <p className="text-xs font-semibold text-gray-900 flex items-center gap-1">
                      {!usingCompanyLogo && <Check className="w-3.5 h-3.5 text-primary-600 shrink-0" />}
                      Custom for card
                    </p>
                    <label className="mt-1.5 btn-secondary text-[11px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer">
                      <Upload className="w-3 h-3" />
                      {customLogoUrl ? 'Change' : 'Upload'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            fd.append('is_private', '0');
                            const res = await saasApi.uploadFile(fd);
                            const url = res.data?.message?.file_url as string;
                            if (url) setDraft((p) => ({ ...p, card_logo: url }));
                            else toast.error('Upload failed');
                          } catch (err) {
                            toast.error(getApiErrorMessage(err, 'Upload failed'));
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
                {!companyLogoUrl && usingCompanyLogo && (
                  <p className="text-[11px] text-amber-700 mt-2">
                    No company logo set yet — add one under Settings → Company, or upload a card logo here.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-center sm:justify-end bg-slate-50 rounded-xl p-4 border border-slate-100">
              <LoyaltyMemberCard
                preview
                design={draft.card_design}
                colour={draft.card_colour}
                logoUrl={logoPreview || null}
                shopName={shopName}
                tagline={draft.card_tagline}
                programName="Loyalty Rewards"
                customerName="Sample Member"
                memberId="CS-0001"
                points={200}
                value={2}
                tier={previewTier}
                showPoints={draft.card_show_points}
                showValue={draft.card_show_value}
                showTier={draft.card_show_tier}
                qrDataUrl="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Crect fill='%23f1f5f9' width='160' height='160'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%2394a3b8' font-size='14'%3EQR%3C/text%3E%3C/svg%3E"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-2xl">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            disabled={saving}
            onClick={() => void onSave(draft)}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save card design
          </button>
        </div>
      </div>
    </div>
  );
}
